/**
 * Which voice the story timeline is set to, read off the timeline itself.
 *
 * The one read behind every Creator Studio surface that has to answer "what
 * voice did this person choose", which is what makes it impossible for two of
 * them to hold different answers on one screen. It reads the same three stored
 * options the publish path reads, so what the screen reports is what a publish
 * delivers.
 *
 * A null language means nobody has used the Voice control on this story. That
 * is a distinct state, not a spelling of Original: the publish treats a story
 * with no saved position differently from one whose person chose Original.
 *
 * Because it is only ever a read of the timeline, re-run on the notifications
 * after which the timeline may be playing a different voice, it cannot drift
 * from what is heard: a saved story reopens with the truth and Undo moves the
 * answer back.
 */
import { EditorChangeType } from '@src/editor/types';
import { useEditor } from '@src/stores-v2/StoreContext';
import { useCallback, useEffect, useState } from 'react';

import type { DubVoiceMode } from '@src/editor/dubbing/DubEngine';
import type { EditorObserver } from '@src/editor/types';

/** The voice position on the timeline, and whose dub it is. */
export interface AppliedDub {
  /** The language whose dub the timeline plays, or `null` when none is chosen. */
  language: string | null;
  /** `original` when there is no dub. */
  mode: DubVoiceMode;
}

/** Nobody has used the Voice control here. */
export const NO_DUB: AppliedDub = { language: null, mode: 'original' };

/**
 * The editor notifications after which the timeline may be playing a different
 * voice. `PROJECT_LOADED` is what a reopened story and a restored undo snapshot
 * both arrive through, which is what makes a saved dubbed story come back
 * reading Dubbed and an Undo press move the control back.
 */
export const TIMELINE_MAY_HAVE_MOVED: EditorChangeType[] = [
  EditorChangeType.TIMELINE_CHANGED,
  EditorChangeType.PROJECT_LOADED,
  EditorChangeType.ELEMENT_ADDED,
  EditorChangeType.ELEMENT_REMOVED,
  EditorChangeType.ELEMENT_UPDATED,
  EditorChangeType.COMMAND_UNDONE,
  EditorChangeType.COMMAND_REDONE,
];

export interface AppliedStoryDubRead {
  appliedDub: AppliedDub;
  /**
   * Re-read right now, for a caller that just ran a command and hands the
   * control back in the same breath. The editor notification arrives too, but
   * this is what keeps the paint from being a frame behind.
   */
  resync: () => void;
}

export function useAppliedStoryDub(): AppliedStoryDubRead {
  const editorStore = useEditor();

  const readStoryVoice = useCallback((): AppliedDub => {
    const options = editorStore.editor?.timeline.dubOptions;
    if (!options) return NO_DUB;
    return { language: options.language, mode: options.voiceMode };
  }, [editorStore]);

  const [appliedDub, setAppliedDub] = useState<AppliedDub>(readStoryVoice);

  // Re-read only when the answer has actually changed, so an unrelated timeline
  // edit does not re-render the surface above.
  const resync = useCallback(() => {
    setAppliedDub(previous => {
      const next = readStoryVoice();
      return previous.language === next.language && previous.mode === next.mode
        ? previous
        : next;
    });
  }, [readStoryVoice]);

  useEffect(() => {
    // A story loads after this hook first renders, so the read on mount alone
    // would report Original for a saved dubbed story.
    resync();

    const editor = editorStore.editor;
    if (!editor) return undefined;

    const observer: EditorObserver = {
      onEditorChange: () => resync(),
    };
    editor.registerObserver(observer, TIMELINE_MAY_HAVE_MOVED);
    return () => editor.unregisterObserver(observer, TIMELINE_MAY_HAVE_MOVED);
  }, [editorStore, resync]);

  return { appliedDub, resync };
}
